import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  BarChart3, ShieldCheck, Loader2, RefreshCw, TrendingUp,
  TrendingDown, Minus, Users,
} from "lucide-react";

/**
 * /admin/profitability — Phase 2.1N.3 Admin Profitability Heat-map.
 *
 * Reads `GET /api/admin/profitability-heatmap?window=24h|7d|30d`,
 * aggregated over the `llm_usage_ledger.profitability_bucket` field.
 *
 * Bucket → colour contract (must match `_maybe_finalize_dynamic_cost`
 * inside `backend/dynamic_credit_metering.py`):
 *   profitable  → GREEN  (net margin > provider_cost_eur * 0.5)
 *   break_even  → YELLOW (net margin > 0 but ≤ provider_cost * 0.5)
 *   loss        → RED    (net margin ≤ 0)
 *
 * Access is server-enforced via `require_admin`; this component adds a
 * client-side guard so non-admins get redirected before rendering.
 * The `pineforgeai@gmx.de` seed identity is admin by default.
 */
const WINDOWS = [
  { key: "24h", label: "Last 24h" },
  { key: "7d",  label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

const BUCKET_META = {
  profitable: {
    label: "Profitable",
    tone: "green",
    icon: TrendingUp,
    hint: "Net margin > 50% of provider cost",
  },
  break_even: {
    label: "Break-even",
    tone: "yellow",
    icon: Minus,
    hint: "Net margin > 0 but ≤ 50% of cost",
  },
  loss: {
    label: "Loss",
    tone: "red",
    icon: TrendingDown,
    hint: "Net margin ≤ 0",
  },
};

const TONE_CLASSES = {
  green:  { card: "border-emerald-500/40 bg-emerald-500/5", accent: "text-emerald-300",  icon: "text-emerald-400" },
  yellow: { card: "border-amber-500/40   bg-amber-500/5",   accent: "text-amber-300",    icon: "text-amber-400" },
  red:    { card: "border-red-500/40     bg-red-500/5",     accent: "text-red-300",      icon: "text-red-400" },
};


export default function AdminProfitability() {
  const { user } = useAuth();
  const [win, setWin] = useState("24h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async (w) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/admin/profitability-heatmap", { params: { window: w } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load heat-map");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(win); }, [win, fetchData]);

  // Client-side gate (backend is the real enforcer).
  if (user && user.is_admin !== true) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-profitability-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            Admin — Profitability Heat-map
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Realised per-action gross margin aggregated over the
            <code className="mx-1 text-zinc-300">llm_usage_ledger</code>
            profitability buckets. Read-only; never touches Stripe.
          </p>
          <nav className="flex items-center gap-1 mt-3" data-testid="admin-tab-nav">
            <Link
              to="/admin/users"
              className="text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded border border-zinc-800 text-zinc-400 hover:border-blue-400/40 hover:text-blue-300 transition-colors flex items-center gap-1"
              data-testid="admin-tab-users-link"
            >
              <Users className="w-3 h-3" /> Users
            </Link>
            <span
              className="text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded border border-blue-400/40 bg-blue-400/10 text-blue-300 flex items-center gap-1"
              data-testid="admin-tab-profitability-active"
            >
              <BarChart3 className="w-3 h-3" /> Profitability
            </span>
          </nav>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData(win)}
          data-testid="admin-profitability-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Window picker */}
      <div className="flex items-center gap-2 mb-4" data-testid="admin-profitability-window-picker">
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            type="button"
            data-testid={`admin-profitability-window-${w.key}`}
            onClick={() => setWin(w.key)}
            className={
              "px-3 py-1.5 rounded text-xs font-mono uppercase tracking-wider transition-colors border " +
              (win === w.key
                ? "border-blue-400/40 bg-blue-400/10 text-blue-300"
                : "border-zinc-800 text-zinc-400 hover:border-blue-400/40 hover:text-blue-300")
            }
          >
            {w.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-12" data-testid="admin-profitability-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Aggregating…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md border border-red-500/40 bg-red-500/5 p-3 text-red-200 text-sm" data-testid="admin-profitability-error">
          {String(error)}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Bucket cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="admin-profitability-buckets">
            {["profitable", "break_even", "loss"].map((b) => (
              <BucketCard
                key={b}
                bucket={b}
                stats={data.buckets?.[b]}
                totalsRows={data.totals?.rows || 0}
              />
            ))}
          </div>

          {/* Totals row */}
          <TotalsRow totals={data.totals} generatedAt={data.generated_at} window={data.window} />

          {/* Top tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <TopTable
              title="Top 10 — Highest margin"
              testId="admin-top-positive"
              rows={data.top_positive || []}
              positive
            />
            <TopTable
              title="Top 10 — Lowest margin"
              testId="admin-top-negative"
              rows={data.top_negative || []}
              positive={false}
            />
          </div>
        </>
      )}
    </div>
  );
}


function BucketCard({ bucket, stats, totalsRows }) {
  const meta = BUCKET_META[bucket];
  const tone = TONE_CLASSES[meta.tone];
  const Icon = meta.icon;
  const rows = stats?.rows ?? 0;
  const margin = stats?.estimated_margin_eur ?? 0;
  const credits = stats?.credits_charged ?? 0;
  const share = stats?.share_pct ?? 0;
  return (
    <div
      className={`rounded-xl border p-4 backdrop-blur-sm ${tone.card}`}
      data-testid={`admin-bucket-${bucket}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${tone.icon}`} />
          <span className={`text-sm font-medium ${tone.accent}`}>{meta.label}</span>
        </div>
        <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
          {share.toFixed(1)}% share
        </span>
      </div>
      <div className="text-3xl font-semibold text-zinc-100" data-testid={`admin-bucket-${bucket}-rows`}>
        {rows}
      </div>
      <div className="text-[11px] text-zinc-500 font-mono mt-1">
        {rows === 1 ? "action" : "actions"} · {credits} credits · {totalsRows ? `${share.toFixed(1)}% of total` : "no data"}
      </div>
      <div className="mt-3 pt-3 border-t border-white/5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Net margin
        </div>
        <div className={`text-xl font-semibold ${tone.accent}`} data-testid={`admin-bucket-${bucket}-margin`}>
          {formatEur(margin)}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">{meta.hint}</div>
      </div>
    </div>
  );
}


function TotalsRow({ totals, generatedAt, window: w }) {
  if (!totals) return null;
  return (
    <div
      className="rounded-xl border border-[#1F2937] bg-[#09090B]/90 backdrop-blur-sm p-4 flex flex-wrap items-center gap-6"
      data-testid="admin-profitability-totals"
    >
      <TotalCell label="Total actions" value={totals.rows} testid="admin-totals-rows" />
      <TotalCell label="Credits charged" value={totals.credits_charged} testid="admin-totals-credits" />
      <TotalCell label="Provider cost" value={formatEur(totals.provider_cost_eur)} testid="admin-totals-cost" />
      <TotalCell label="Revenue estimate" value={formatEur(totals.margin_revenue_eur)} testid="admin-totals-revenue" />
      <TotalCell
        label="Net margin"
        value={formatEur(totals.estimated_margin_eur)}
        testid="admin-totals-margin"
        highlight={totals.estimated_margin_eur > 0 ? "green" : "red"}
      />
      <div className="ml-auto text-[10px] text-zinc-500 font-mono">
        {w} · generated {new Date(generatedAt).toLocaleTimeString()}
      </div>
    </div>
  );
}


function TotalCell({ label, value, testid, highlight }) {
  const cls = highlight === "green"
    ? "text-emerald-300"
    : highlight === "red"
      ? "text-red-300"
      : "text-zinc-100";
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
    </div>
  );
}


function TopTable({ title, rows, positive, testId }) {
  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#09090B]/90 backdrop-blur-sm" data-testid={testId}>
      <div className="px-4 py-3 border-b border-[#1F2937] flex items-center gap-2">
        {positive
          ? <TrendingUp className="w-4 h-4 text-emerald-400" />
          : <TrendingDown className="w-4 h-4 text-red-400" />}
        <h2 className="text-sm font-medium text-zinc-100">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-zinc-500 text-sm">No data for this window.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-2 text-left">When</th>
              <th className="px-4 py-2 text-left">Action</th>
              <th className="px-4 py-2 text-right">Credits</th>
              <th className="px-4 py-2 text-right">Cost</th>
              <th className="px-4 py-2 text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-zinc-800/60" data-testid={`admin-top-row-${r.id}`}>
                <td className="px-4 py-2 text-zinc-500 font-mono text-[10px]">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-2 text-zinc-300 truncate max-w-[200px]" title={r.action_type}>
                  {(r.action_type || "").replace("__dynamic_finalize", "")}
                </td>
                <td className="px-4 py-2 text-right text-zinc-200 font-mono">{r.credits_charged}</td>
                <td className="px-4 py-2 text-right text-zinc-400 font-mono">{formatEur(r.provider_cost_eur)}</td>
                <td className={
                  "px-4 py-2 text-right font-mono " +
                  (r.estimated_margin_eur >= 0 ? "text-emerald-300" : "text-red-300")
                }>
                  {formatEur(r.estimated_margin_eur)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function formatEur(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}€${Math.abs(v).toFixed(4)}`;
}
